import { useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon, Image, ShoppingBag, Gamepad2, Radio, Key,
  Users, Activity, Mail, AlertTriangle, Clock
} from "lucide-react";

const adminPermissions = [
  { key: "daily_code", label: "Dagens Adgangskode", icon: Key, description: "Generer og del en dagskode til nye medlemmer", color: "green" },
  { key: "members_list", label: "Medlemmer", icon: Users, description: "Se og administrer alle medlemmer", color: "blue" },
  { key: "activity", label: "Aktivitet & Statistik", icon: Activity, description: "Login-historik og enhedsinfo", color: "purple" },
  { key: "live_section", label: "Live Sektion", icon: Radio, description: "Styr Live-sektionen på forsiden", color: "orange" },
  { key: "bookings", label: "Bookingforespørgsler", icon: CalendarIcon, description: "Håndter bookinger", color: "blue" },
  { key: "messages", label: "Kontaktindbakke", icon: Mail, description: "Læs og besvar beskeder", color: "green" },
];

const memberPermissions = [
  { key: "my_code", label: "Min Kode", icon: Key, description: "Se og skift din personlige adgangskode", color: "blue" },
  { key: "nav_calendar", label: "Kalender", icon: CalendarIcon, description: "Se events", color: "blue", navigate: "/kalender" },
  { key: "nav_gallery", label: "Galleri", icon: Image, description: "Se og upload billeder", color: "pink", navigate: "/galleri" },
  { key: "nav_webshop", label: "Webshop", icon: ShoppingBag, description: "Se klubprodukter", color: "emerald", navigate: "/webshop" },
  { key: "nav_game", label: "Spil", icon: Gamepad2, description: "Spil med andre medlemmer", color: "violet", navigate: "/spil" },
];

const colorClasses = (c) => {
  const map = {
    purple: "bg-purple-600/20 text-purple-400", blue: "bg-blue-600/20 text-blue-400",
    green: "bg-green-600/20 text-green-400", pink: "bg-pink-600/20 text-pink-400",
    orange: "bg-orange-600/20 text-orange-400", emerald: "bg-emerald-600/20 text-emerald-400",
    violet: "bg-violet-600/20 text-violet-400"
  };
  return map[c] || "bg-gray-600/20 text-gray-400";
};

const getCodeExpiryStatus = (dailyCode) => {
  if (!dailyCode?.active || !dailyCode?.expires_at) return null;
  const diff = new Date(dailyCode.expires_at) - new Date();
  if (diff <= 0) return { level: "expired", label: "Dagskoden er udløbet" };
  const hours = diff / (1000 * 60 * 60);
  if (hours <= 1) return { level: "critical", label: "Dagskoden udløber om under 1 time" };
  if (hours <= 2) return { level: "warning", label: "Dagskoden udløber snart" };
  return null;
};

const OverviewTab = ({ isAdmin, loading, members, bookings, contactMessages, setActiveTab, dailyCode, dailyCountdown }) => {
  const navigate = useNavigate();
  const myPermissions = isAdmin ? [...adminPermissions, ...memberPermissions] : memberPermissions;
  const codeStatus = isAdmin ? getCodeExpiryStatus(dailyCode) : null;
  
  const unansweredMessages = contactMessages.filter(m => m.status !== "answered").length;
  const pendingBookings = bookings.filter(b => b.status === "pending").length;

  return (
    <div>
      {/* Expiry notification banner */}
      {codeStatus && (
        <button
          onClick={() => setActiveTab("daily_code")}
          className={`w-full mb-6 p-4 rounded-lg flex items-center gap-3 transition-all hover:scale-[1.01] ${
            codeStatus.level === "expired" ? "bg-red-600/20 border border-red-500/40" :
            codeStatus.level === "critical" ? "bg-red-600/15 border border-red-500/30 animate-pulse" :
            "bg-yellow-600/15 border border-yellow-500/30"
          }`}
          data-testid="daily-code-expiry-alert"
        >
          {codeStatus.level === "expired" ? (
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          ) : (
            <Clock className={`w-5 h-5 flex-shrink-0 ${codeStatus.level === "critical" ? "text-red-400" : "text-yellow-400"}`} />
          )}
          <div className="flex-1 text-left">
            <p className={`text-sm font-medium ${codeStatus.level === "expired" || codeStatus.level === "critical" ? "text-red-300" : "text-yellow-300"}`}>
              {codeStatus.label}
            </p>
            {dailyCountdown && codeStatus.level !== "expired" && (
              <p className="text-xs text-gray-400 mt-0.5">{dailyCountdown} tilbage</p>
            )}
          </div>
          <span className={`text-xs px-3 py-1 rounded-full ${
            codeStatus.level === "expired" ? "bg-red-600/30 text-red-300" :
            codeStatus.level === "critical" ? "bg-red-600/30 text-red-300" :
            "bg-yellow-600/30 text-yellow-300"
          }`}>
            {codeStatus.level === "expired" ? "Generer ny" : "Se kode"}
          </span>
        </button>
      )}

      <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-6">Din adgang</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {myPermissions.map((perm) => {
          const badge = perm.key === "bookings" ? pendingBookings : perm.key === "messages" ? unansweredMessages : 0;
          return (
            <button key={perm.key}
              onClick={() => perm.navigate ? navigate(perm.navigate) : setActiveTab(perm.key)}
              className="mc-card p-6 text-left hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 group relative"
              data-testid={`permission-${perm.key}`}>
              {badge > 0 && (
                <span className="absolute top-3 right-3 min-w-[24px] h-[24px] flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1.5 animate-pulse" data-testid={`badge-${perm.key}`}>
                  {badge}
                </span>
              )}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colorClasses(perm.color).split(' ')[0]}`}>
                <perm.icon size={24} className={colorClasses(perm.color).split(' ')[1]} />
              </div>
              <h3 className="text-white font-['Oswald'] text-lg group-hover:text-blue-400 transition-colors mb-1">{perm.label}</h3>
              <p className="text-gray-500 text-sm">{perm.description}</p>
              {badge > 0 && <p className="text-red-400 text-xs mt-2 font-medium">{badge} {perm.key === "bookings" ? "afventer svar" : "ubesvarede"}</p>}
            </button>
          );
        })}
      </div>

      {isAdmin && !loading && (
        <div className="grid sm:grid-cols-4 gap-4 mt-8">
          <div className="mc-card p-6 text-center">
            <p className="text-4xl font-bold text-blue-400 mb-2">{members.length}</p>
            <p className="text-gray-400 text-sm">Medlemmer</p>
          </div>
          <div className="mc-card p-6 text-center">
            <p className="text-4xl font-bold text-green-400 mb-2">{members.filter(m => m.is_active !== false).length}</p>
            <p className="text-gray-400 text-sm">Aktive</p>
          </div>
          <div className="mc-card p-6 text-center">
            <p className="text-4xl font-bold text-purple-400 mb-2">{bookings.length}</p>
            <p className="text-gray-400 text-sm">Bookinger</p>
          </div>
          <div className="mc-card p-6 text-center">
            <p className="text-4xl font-bold text-orange-400 mb-2">{contactMessages.filter(m => m.status === "new").length}</p>
            <p className="text-gray-400 text-sm">Nye beskeder</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
